import React, { useRef, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    CheckCircle2, XCircle, ArrowRight, ChevronRight, AlertTriangle,
    Zap, Activity, FileText
} from 'lucide-react';
import { PAGES, ALL_FEATURES } from './featurePageData';

// ─── Lazy Video ──────────────────────────────────────────────────────────────
const LazyVideo: React.FC<{ src: string; poster?: string; className?: string; label?: string }> = ({ src, poster, className, label }) => {
    const ref = useRef<HTMLVideoElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting) { setInView(true); ref.current?.play().catch(() => { }); }
            else ref.current?.pause();
        }, { threshold: 0.1, rootMargin: '200px' });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);
    return (
        <video ref={ref} className={className} poster={inView ? poster : undefined} muted loop playsInline preload="metadata" aria-label={label}>
            {inView && <source src={src} type="video/mp4" />}
        </video>
    );
};

// ─── Process Flow Diagram ─────────────────────────────────────────────────────
const ProcessFlow: React.FC<{ steps: { step: string; title: string; desc: string }[]; color: string }> = ({ steps, color }) => (
    <div className="relative">
        {/* Vertical connector line */}
        <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-transparent via-white/10 to-transparent hidden md:block" />
        <div className="space-y-4">
            {steps.map((s, i) => (
                <div key={i} className="flex gap-4 items-start group">
                    <div className="relative z-10 w-12 h-12 rounded-xl flex items-center justify-center font-black text-sm shrink-0 border transition-all group-hover:scale-110"
                        style={{ background: `${color}15`, borderColor: `${color}40`, color }}>
                        {s.step}
                    </div>
                    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 flex-1 hover:border-white/10 transition-all">
                        <div className="font-black text-white mb-1 text-sm">{s.title}</div>
                        <div className="text-gray-400 text-xs leading-relaxed">{s.desc}</div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// ─── Comparison Table ─────────────────────────────────────────────────────────
const ComparisonTable: React.FC<{ rows: { label: string; us: boolean; them: boolean }[]; color: string }> = ({ rows, color }) => (
    <div className="overflow-hidden rounded-2xl border border-white/10">
        <div className="grid grid-cols-3 text-xs font-black uppercase tracking-widest py-3 px-4 bg-white/[0.05] border-b border-white/5">
            <div className="col-span-1 text-gray-500">Feature</div>
            <div className="text-center" style={{ color }}>ForexFund</div>
            <div className="text-center text-gray-600">Others</div>
        </div>
        {rows.map((r, i) => (
            <div key={i} className={`grid grid-cols-3 py-3 px-4 text-sm items-center ${i % 2 === 0 ? 'bg-white/[0.01]' : ''} hover:bg-white/[0.03] transition-colors`}>
                <div className="col-span-1 text-gray-300 text-xs leading-relaxed pr-4">{r.label}</div>
                <div className="flex justify-center">
                    {r.us ? <CheckCircle2 className="w-5 h-5" style={{ color }} /> : <XCircle className="w-5 h-5 text-red-500/60" />}
                </div>
                <div className="flex justify-center">
                    {r.them ? <CheckCircle2 className="w-5 h-5 text-gray-600" /> : <XCircle className="w-5 h-5 text-red-500/60" />}
                </div>
            </div>
        ))}
    </div>
);

// ─── FOMO Section ─────────────────────────────────────────────────────────────
const FOMOSection: React.FC<{ data: { heading: string; body: string; points: string[] }; color: string }> = ({ data, color }) => (
    <div className="bg-red-950/20 border border-red-500/20 rounded-3xl p-8 md:p-12">
        <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-red-500/10 rounded-xl shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-black text-white leading-tight">{data.heading}</h2>
        </div>
        <p className="text-gray-400 leading-relaxed mb-8 text-base max-w-3xl">{data.body}</p>
        <div className="grid md:grid-cols-2 gap-3">
            {data.points.map((p, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-red-500/5 border border-red-500/10 rounded-xl">
                    <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm leading-relaxed">{p}</span>
                </div>
            ))}
        </div>
    </div>
);

// ─── Autotrade Flow ───────────────────────────────────────────────────────────
const AutotradeFlow: React.FC<{ data: { heading: string; body: string; steps: string[] }; color: string }> = ({ data, color }) => (
    <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-8 md:p-12">
        <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
                <Activity className="w-5 h-5" style={{ color }} />
            </div>
            <h2 className="text-2xl font-black text-white">{data.heading}</h2>
        </div>
        <p className="text-gray-400 mb-8">{data.body}</p>
        <div className="relative pl-6 border-l-2 space-y-6" style={{ borderColor: `${color}30` }}>
            {data.steps.map((step, i) => (
                <div key={i} className="relative flex items-start gap-4">
                    <div className="absolute -left-[25px] w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0"
                        style={{ background: '#060810', borderColor: color }}>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                    </div>
                    <p className="text-gray-300 text-sm leading-relaxed">{step}</p>
                </div>
            ))}
        </div>
    </div>
);

// ─── Telegram Join Section ───────────────────────────────────────────────────
const TelegramCTA: React.FC<{ data: { heading: string; body: string; link: string }; color: string }> = ({ data, color }) => (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 p-10 md:p-16 text-center shadow-2xl group"
        style={{
            background: 'rgba(26, 31, 46, 0.8)',
            backdropFilter: 'blur(12px)',
            boxShadow: `0 0 50px ${color}15`
        }}>
        {/* Ambient Glow */}
        <div className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity duration-700 pointer-events-none"
             style={{ background: `radial-gradient(circle at 50% 0%, ${color} 0%, transparent 70%)` }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col items-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-8 shadow-lg border"
                 style={{ background: `${color}20`, borderColor: `${color}40`, boxShadow: `0 0 40px ${color}30` }}>
                <svg className="w-10 h-10" fill="none" stroke={color} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
            </div>
            
            <h2 className="text-3xl md:text-5xl font-black text-white mb-6 tracking-tight drop-shadow-xl" style={{ fontFamily: 'Cinzel, serif' }}>
                {data.heading}
            </h2>
            <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed font-medium">
                {data.body}
            </p>
            
            <a href={data.link} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center justify-center px-12 py-6 rounded-2xl text-black font-black text-sm uppercase tracking-[3px] hover:scale-105 transition-all outline-none shadow-2xl"
               style={{ background: `linear-gradient(to right, #FDE047, ${color})`, boxShadow: `0 10px 40px -10px ${color}` }}>
                JOIN OFFICIAL TELEGRAM
            </a>
            
            <p className="mt-8 text-xs text-gray-500 font-bold uppercase tracking-[2px]">
                Trusted by 5,000+ Institutional Traders
            </p>
        </div>
    </div>
);

// ─── Main FeatureDetail Component ─────────────────────────────────────────────
export const FeatureDetail: React.FC = () => {
    const { featureId } = useParams<{ featureId: string }>();
    const navigate = useNavigate();
    const page = featureId ? PAGES[featureId] : null;

    if (!page) {
        return (
            <div className="min-h-screen bg-[#060810] flex flex-col items-center justify-center p-4">
                <h1 className="text-2xl text-white font-bold mb-4">Feature Not Found</h1>
                <Link to="/" className="text-[#D4A537] hover:underline">Return Home</Link>
            </div>
        );
    }

    const Icon = page.icon;
    const c = page.hero.color;
    const others = ALL_FEATURES.filter(f => f.id !== featureId);

    return (
        <>
            <Helmet>
                <title>{page.seo.title}</title>
                <meta name="description" content={page.seo.description} />
                <meta name="keywords" content={page.seo.keywords} />
                <meta property="og:title" content={page.seo.title} />
                <meta property="og:description" content={page.seo.description} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content={page.desktopPoster} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={page.seo.title} />
                <meta name="twitter:description" content={page.seo.description} />
                <link rel="canonical" href={`https://www.forexfundai.com/features/${featureId}`} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": [{
                            "@type": "Question",
                            "name": page.featuredSnippet.question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": page.featuredSnippet.answer
                            }
                        }]
                    })}
                </script>
            </Helmet>

            <div className="min-h-screen bg-[#060810] text-white">

                {/* ── HERO ──────────────────────────────────────────────────── */}
                <section className="relative py-32 px-4 overflow-hidden">
                    <div className="absolute inset-0 bg-[#060810]" />
                    <div className="absolute inset-0" style={{ 
                        backgroundImage: `linear-gradient(to right, #ffffff05 1px, transparent 1px), linear-gradient(to bottom, #ffffff05 1px, transparent 1px)`, 
                        backgroundSize: '80px 80px',
                        maskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, black 30%, transparent 100%)'
                    }} />
                    <div className="absolute inset-x-0 top-[-20%] h-[1000px] opacity-[0.35] blur-[100px] pointer-events-none" style={{ background: `radial-gradient(circle at 50% 20%, ${c} 0%, transparent 60%)` }} />
                    <div className="max-w-5xl mx-auto text-center relative z-10 pt-16 pb-12">
                        <span className="inline-block px-5 py-2.5 rounded-full text-xs font-black tracking-[4px] uppercase mb-8 border backdrop-blur-md shadow-2xl"
                            style={{ color: '#FDE047', borderColor: `${c}50`, background: `${c}20`, boxShadow: `0 0 30px ${c}30` }}>
                            {page.hero.badge}
                        </span>
                        <h1 className="text-4xl md:text-7xl lg:text-[5.5rem] font-black mb-8 tracking-tighter leading-[1.0] text-white drop-shadow-2xl">
                            {page.hero.headline}
                        </h1>
                        <p className="text-lg md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-14 drop-shadow-md font-medium">
                            {page.hero.sub}
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
                            <button onClick={() => navigate(page.cta.href)}
                                className="px-10 py-5 rounded-xl text-black font-black text-sm uppercase tracking-[2px] hover:scale-105 transition-all outline-none focus:outline-none"
                                style={{ background: `linear-gradient(to right, #FDE047, ${c})`, boxShadow: `0 10px 40px -10px ${c}` }}>
                                {page.cta.text}
                            </button>
                            <button onClick={() => navigate('/login')}
                                className="px-10 py-5 rounded-xl border border-white/20 bg-white/5 text-white font-bold tracking-wide hover:bg-white/10 hover:border-white/40 transition-all shadow-xl backdrop-blur-md outline-none focus:outline-none">
                                Sign In Free →
                            </button>
                        </div>
                    </div>
                </section>

                {/* ── STATS BAR ─────────────────────────────────────────────── */}
                <section className="py-10 border-y border-white/5 bg-white/[0.015]">
                    <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
                        {page.stats.map(s => (
                            <div key={s.label} className="text-center">
                                <div className="text-3xl md:text-4xl font-black mb-1" style={{ color: c }}>
                                    {s.value}{s.suffix && <span className="text-xl">{s.suffix}</span>}
                                </div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* LIVE DEMO REMOVED PER USER REQUEST */}

                {/* ── THE PROBLEM ───────────────────────────────────────────── */}
                <section className="py-20 px-4">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-12">
                            <span className="text-xs font-black uppercase tracking-[3px] text-red-400 mb-3 block">The Real Problem</span>
                            <h2 className="text-3xl md:text-5xl font-black text-white leading-tight">{page.problem.heading}</h2>
                        </div>
                        <div className="grid md:grid-cols-2 gap-8 items-start">
                            <p className="text-gray-400 leading-relaxed text-base md:text-lg">{page.problem.body}</p>
                            <div className="space-y-3">
                                <div className="text-xs font-black uppercase tracking-widest text-red-400 mb-4">Why Traders Fail At This:</div>
                                {page.problem.failReasons.map((r, i) => (
                                    <div key={i} className="flex items-start gap-3 p-3 bg-red-500/5 border border-red-500/10 rounded-xl">
                                        <XCircle className="w-4 h-4 text-red-500/70 shrink-0 mt-0.5" />
                                        <span className="text-gray-300 text-sm leading-relaxed">{r}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── HOW IT WORKS ──────────────────────────────────────────── */}
                <section className="py-20 px-4 bg-white/[0.015] border-y border-white/5">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-12">
                            <span className="text-xs font-black uppercase tracking-[3px] mb-3 block" style={{ color: c }}>How It Works</span>
                            <h2 className="text-3xl md:text-5xl font-black text-white">{page.howItWorks.heading}</h2>
                        </div>
                        <ProcessFlow steps={page.howItWorks.steps} color={c} />
                    </div>
                </section>

                {/* ── DEEP CONTENT ──────────────────────────────────────────── */}
                <section className="py-20 px-4">
                    <div className="max-w-5xl mx-auto space-y-20">
                        {page.deepContent.map((sec, i) => (
                            <div key={i} className={`grid md:grid-cols-2 gap-10 items-center ${i % 2 === 1 ? 'md:[&>*:first-child]:order-2' : ''}`}>
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-black text-white mb-5 leading-tight">{sec.heading}</h2>
                                    <p className="text-gray-400 leading-relaxed text-base">{sec.body}</p>
                                    {sec.bullets && (
                                        <div className="mt-6 space-y-3">
                                            {sec.bullets.map((b, bi) => (
                                                <div key={bi} className="flex items-start gap-3">
                                                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: c }} />
                                                    <span className="text-gray-300 text-sm leading-relaxed">{b}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {sec.image ? (
                                    <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl" style={{ boxShadow: `0 0 60px ${c}10` }}>
                                        <img src={sec.image.src} alt={sec.image.alt} className="w-full object-cover" loading="lazy" />
                                    </div>
                                ) : sec.bullets ? (
                                    <div className="hidden md:block" />
                                ) : (
                                    <div className="hidden md:block" />
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* SCREENSHOT SECTION REMOVED PER USER REQUEST */}

                {/* ── WHY US — COMPARISON TABLE ─────────────────────────────── */}
                <section className="py-20 px-4 bg-white/[0.015] border-y border-white/5">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-10">
                            <span className="text-xs font-black uppercase tracking-[3px] mb-3 block" style={{ color: c }}>Why ForexFund</span>
                            <h2 className="text-3xl md:text-4xl font-black text-white">{page.whyUs.heading}</h2>
                            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">{page.whyUs.body}</p>
                        </div>
                        <ComparisonTable rows={page.whyUs.comparison} color={c} />
                    </div>
                </section>

                {/* ── AUTOTRADE FLOW (if available) ─────────────────────────── */}
                {page.autotrade && (
                    <section className="py-20 px-4">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-10">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border mb-4"
                                    style={{ color: c, borderColor: `${c}30`, background: `${c}10` }}>
                                    <Zap className="w-3 h-3" /> Autotrade Integration
                                </div>
                            </div>
                            <AutotradeFlow data={page.autotrade} color={c} />
                        </div>
                    </section>
                )}

                {/* ── FOMO ────────────────────────────────────────────────────── */}
                <section className="py-20 px-4">
                    <div className="max-w-4xl mx-auto">
                        <FOMOSection data={page.fomo} color={c} />
                    </div>
                </section>

                {/* ── CTA ───────────────────────────────────────────────────── */}
                <section className="py-28 px-4 text-center relative overflow-hidden">
                    <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 60% at 50% 100%, ${c}12 0%, transparent 70%)` }} />
                    <div className="relative z-10 max-w-2xl mx-auto">
                        <div className="inline-flex p-4 rounded-3xl mb-8" style={{ background: `${c}15`, border: `1px solid ${c}30` }}>
                            <Icon className="w-10 h-10" style={{ color: c }} />
                        </div>
                        <h2 className="text-3xl md:text-5xl font-black text-white mb-4">Ready to Trade Like an Institution?</h2>
                        <p className="text-gray-400 mb-8 text-lg max-w-xl mx-auto">
                            Get full access to {page.hero.badge} plus every other ForexFund module — all in one plan.
                        </p>
                        <button onClick={() => navigate(page.cta.href)}
                            className="px-10 py-5 rounded-xl text-black font-black text-sm uppercase tracking-widest hover:scale-105 transition-all"
                            style={{ background: `linear-gradient(135deg, ${c}, ${c}bb)`, boxShadow: `0 0 50px ${c}35` }}>
                            {page.cta.text} <ArrowRight className="inline ml-2 w-4 h-4" />
                        </button>
                    </div>
                </section>

                {/* ── INSTITUTIONAL Q&A / SNIPPET ─────────────────────────── */}
                <section className="py-20 px-4 border-t border-white/5">
                    <div className="max-w-4xl mx-auto">
                        <div className="p-8 md:p-12 rounded-3xl bg-white/[0.02] border border-white/10 relative overflow-hidden group">
                           <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
                                <FileText className="w-24 h-24" style={{ color: c }} />
                           </div>
                           <h2 className="text-2xl font-black text-white mb-6 uppercase tracking-tight flex items-center gap-3">
                                <span className="w-8 h-px bg-white/20" />
                                Institutional Q&A
                           </h2>
                           <div className="space-y-6 relative z-10">
                                <div className="max-w-3xl">
                                    <h3 className="text-xl font-bold mb-4" style={{ color: c }}>{page.featuredSnippet.question}</h3>
                                    <p className="text-gray-400 leading-relaxed text-lg italic border-l-4 border-white/10 pl-6 py-2">
                                        "{page.featuredSnippet.answer}"
                                    </p>
                                </div>
                           </div>
                        </div>
                    </div>
                </section>

                {/* ── TELEGRAM CTA ──────────────────────────────────────────── */}
                {page.telegramCTA && (
                    <section className="py-20 px-4 border-t border-white/5">
                        <div className="max-w-5xl mx-auto">
                            <TelegramCTA data={page.telegramCTA} color={c} />
                        </div>
                    </section>
                )}

                {/* ── EXPLORE MORE ──────────────────────────────────────────── */}
                <section className="py-20 px-4 border-t border-white/5">
                    <div className="max-w-5xl mx-auto">
                        <h2 className="text-2xl font-black text-white mb-2 text-center">Explore Every Module</h2>
                        <p className="text-gray-500 text-center mb-10 text-sm">Each module works best when combined. The full ForexFund system is greater than the sum of its parts.</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                            {others.map(f => (
                                <Link key={f.id} to={`/features/${f.id}`}
                                    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04] transition-all group text-center">
                                    <f.icon className="w-6 h-6 text-gray-400 group-hover:text-[#D4A537] transition-colors" />
                                    <span className="text-xs font-bold text-gray-500 group-hover:text-white transition-colors leading-tight">{f.label}</span>
                                    <ChevronRight className="w-3 h-3 text-gray-700 group-hover:text-gray-400 transition-colors" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>

            </div>
        </>
    );
};
